import { ProjectFields, YesObject } from '@yes.technology/react-toolkit'
import api from '../../utils/api'
import fetchFilteraction from './fetchFilteraction'

// TODO: Remove after we export these types from toolkit
type PropertyQuery = {
  [operator: string]: string | string[] | undefined
}
type GroupQuery = {
  [propertyPath: string]: PropertyQuery
}

export type FilterQuery = {
  [operator: string]: (GroupQuery | FilterQuery)[]
}

type QueryObject = FilterQuery | GroupQuery | PropertyQuery

// resolveQuery recebe um objeto no padrão YesQueryLanguage,
// padrão inspirado no MongoDB, e resolve recursivamente todos
// os operadores do tipo $filteraction, se existir
//
// Se não existir nenhum operador do tipo $filteraction
// para ser resolvido, simplesmente devolvemos o mesmo
// queryObject
//
// O método aceita um callback onProgressCallback que é chamado
// a cada nova etapa da resolução dos filteractions,
// para permitir que a UI comunique o progresso ao usuário do sistema
//
// O array fetchedFilteractionIds é utilizado para identificar loops infinitos
export async function resolveQuery<T extends QueryObject>(
  queryObjectOriginal: T,
  onProgressCallback?: (progress: string) => void,
  fetchedFilteractionIds: string[] = [],
  showLoading = false
): Promise<T> {
  const queryObject = { ...queryObjectOriginal }
  // Loop recursivo entre todos os objetos da query
  for (const key of Object.keys(queryObject)) {
    if (key === '$in' || key === '$nin') {
      // Nada a fazer
    } else if (queryObject[key] instanceof Array) {
      // Se for um array ($and/$or)

      queryObject[key] = await Promise.all(
        queryObject[key].map((subQuery) =>
          resolveQuery(
            subQuery as GroupQuery | FilterQuery,
            onProgressCallback,
            fetchedFilteractionIds,
            showLoading
          )
        )
      )
    } else if (queryObject[key] instanceof Object) {
      // Se for um objeto
      queryObject[key] = await resolveQuery(
        queryObject[key],
        onProgressCallback,
        fetchedFilteractionIds,
        showLoading
      )
    } else {
      // Se chegou aqui é uma primativa (strings, inteiros, etc)
      if (key === '$filteraction') {
        // Se for uma primativa com chave $filteraction, significa que temos
        // um UUID de uma filteraction, que será consultado no backend,
        // e sua query será resolvida
        const filteractionId = queryObject[key] as string
        if (fetchedFilteractionIds.includes(filteractionId)) {
          queryObject[key] = '__CYCLIC_DEPENDENCY_DETECTED__'
          onProgressCallback?.(
            'Cyclic dependency detected on Filteraction ID ' + filteractionId
          )
        } else {
          fetchedFilteractionIds.push(filteractionId)
          onProgressCallback?.('Fetching Filteraction ID ' + filteractionId)
          const filteraction = await fetchFilteraction(
            filteractionId,
            showLoading
          )

          if (!filteraction) {
            throw new Error('Filteraction not found: ' + filteractionId)
          }

          onProgressCallback?.(
            'Finished fetching Filteraction ID ' + filteractionId
          )
          const query = JSON.parse(filteraction.query)

          const filteractionResolvedQuery = await resolveQuery(
            query,
            onProgressCallback,
            fetchedFilteractionIds,
            showLoading
          )

          delete queryObject[key]

          const objectclassId = (
            filteraction.filter_action_objectclass as YesObject[]
          )[0].uuid
          onProgressCallback?.(
            'Executing query on Objectclass ID ' + objectclassId
          )

          const objectsResponse = await executeQuery({
            objectclassId,
            query: filteractionResolvedQuery,
            projectFields: { uuid: 1, des: 1 },
            showLoading
          })

          onProgressCallback?.(
            'Finished executing query on Objectclass ID ' + objectclassId
          )
          queryObject.$in = objectIds(objectsResponse?.objects || [])
        }
      }
    }
  }
  return { ...queryObject }
}

type ExecuteQueryParam = {
  objectclassId: string
  query: FilterQuery
  skipRelations?: boolean
  skipMetadata?: boolean
  projectFields?: ProjectFields
  showLoading?: boolean
  throwError?: boolean
}

// executeQuery recebe uma objectclassId e uma query, executa
// o fetch no backend, e retorna os objetos encontrados
async function executeQuery({
  objectclassId,
  query,
  skipRelations = true,
  skipMetadata = true,
  projectFields,
  showLoading = false,
  throwError = true
}: ExecuteQueryParam) {
  const response = await api.postObjectsFilter(
    {
      query: query,
      objectclass: objectclassId,
      orderby: [],
      pagination: {
        offset: 0,
        limit: 9999999
      },
      visualization: 'classic-list',
      skip_relations: skipRelations,
      skip_metadata: skipMetadata,
      project_fields: projectFields
    },
    showLoading,
    throwError
  )
  return response
}

function objectIds(objectArray: { id?: string; uuid: string }[]) {
  return objectArray.map((i) => i.id || i.uuid)
}
